import React, { useState, useEffect, MutableRefObject, Dispatch, SetStateAction } from "react";
import _ from "underscore";

import ValidationView from "../validation-view/validation-view";
import ValidationList, { ValidationListProperty } from "../validation-list/validation-list";
import { fetchTables } from "../../select-tables.utils";
import { DB_PROVIDERS } from "app/components/appwizard/component-wizards/wizards/source/database-reader/configs/db-providers";
import { APIStatus, Schema } from "../../schema-table";
import { useSelectTables } from "src/modules/flow-designer/vts/select-tables-context";
import { getNameAndSchema } from "src/modules/wizards/services/wizards-methods-helper";

const leftPosition = {
    control: { type: "select" },
    options: [true, false]
};

interface ValidateSchemaViewProps {
    adapterName: string;
    selectedSchemaTables: Schema[];
    setSelectedSchemaTables: Dispatch<SetStateAction<Schema[]>>;
    selectedSchemaTableColumns: Schema[];
    isEditFlow: boolean;
    formValues: object;
    dbType: string;
    schemaTablesList: MutableRefObject<Schema[]>;
    setApiStatus: Dispatch<SetStateAction<APIStatus>>;
    childRef: any;
    metaObjectId?: string;
    schemaType: string;
    cancelRef: any;
}

const ValidateSchemaView: React.FC<ValidateSchemaViewProps> = ({
    adapterName,
    selectedSchemaTables,
    setSelectedSchemaTables,
    selectedSchemaTableColumns,
    isEditFlow,
    schemaTablesList,
    formValues,
    dbType,
    setApiStatus,
    childRef,
    metaObjectId,
    schemaType,
    cancelRef
}) => {
    const [validationListItems, setValidationListItems] = useState<ValidationListProperty[]>([]);
    const { sourceInfo } = useSelectTables();

    useEffect(() => {
        if (selectedSchemaTables.length) {
            let validationItems: ValidationListProperty[] = [];
            selectedSchemaTables.forEach(schema => {
                validationItems.push({
                    id: "fetch",
                    status: "todo",
                    text: `Fetching Metadata about ${schema.name}`,
                    method: () =>
                        !cancelRef.current
                            ? fetchTables(schema.name, formValues, dbType, metaObjectId, adapterName, onSuccess, onFail)
                            : null
                });
            });
            setValidationListItems(validationItems);
        }
    }, [selectedSchemaTables]);

    const onSuccess = (schemaName: string, tables: string[]) => {
        if (cancelRef.current) return;
        const schemaIndex = schemaTablesList.current.findIndex(schema => schema.name === schemaName);
        if (schemaIndex === -1) return;
        const schemaObj = schemaTablesList.current[schemaIndex];

        let preSelectedSchemaObj;
        let preSelectedTablesByName: Record<string, Tables> | null = null;
        if (isEditFlow && selectedSchemaTableColumns) {
            preSelectedSchemaObj = selectedSchemaTableColumns.find(schema => schema.name === schemaName);
            if (preSelectedSchemaObj) {
                const isAnyTableSelected = preSelectedSchemaObj.tables?.some(table => table.isSelected);
                schemaObj.isSelected = isAnyTableSelected;
                preSelectedTablesByName = {};
                preSelectedSchemaObj.tables?.forEach((t: Tables) => {
                    preSelectedTablesByName![t.name] = t;
                });
            }
        }

        const isMongo = dbType === DB_PROVIDERS.MONGODB.id;
        const isAppAdapter = sourceInfo.isAppAdapter;
        const fetchedTables: Tables[] = [];
        for (let i = 0; i < tables.length; i++) {
            const table = tables[i];
            if (isMongo && (table.indexOf("system.keys") !== -1 || table.indexOf("system.sessions") !== -1)) {
                continue;
            }
            const name = isAppAdapter ? table : getNameAndSchema(table, dbType)[0];
            const preSelected = preSelectedTablesByName ? preSelectedTablesByName[name] : undefined;
            fetchedTables.push({
                id: table,
                name,
                fullTableName: table,
                isValid: true,
                errors: "",
                isSelected: preSelected ? preSelected.isSelected : false
            });
        }

        const updatedSchemaObj = { ...schemaObj, tables: fetchedTables };
        schemaTablesList.current = [
            ...schemaTablesList.current.slice(0, schemaIndex),
            updatedSchemaObj,
            ...schemaTablesList.current.slice(schemaIndex + 1)
        ];
        setSelectedSchemaTables(schemaTablesList.current);
    };
    const onFail = () => {
        if (cancelRef.current) return;
        setApiStatus({
            isAPISuccessful: false,
            isAPICompleted: true,
            canRetry: true
        });
    };

    if (!validationListItems.length) return null;

    const schemaText = schemaType ? (schemaType === "Channel" ? schemaType : `${schemaType}s`) : "Schemas";

    return (
        <ValidationView
            title={`Fetching your ${schemaText}`}
            description={`We are now fetching details about your ${schemaText} and validating them. Depending on the size of your database and the network latency, this may take a few minutes.`}
            validationComponent={
                <ValidationList
                    validationList={validationListItems}
                    left={leftPosition}
                    setAPIStatus={setApiStatus}
                    ref={childRef}
                    cancelRef={cancelRef}
                />
            }
        />
    );
};

export default ValidateSchemaView;
